import { openDB, type IDBPDatabase } from 'idb';
import type { SceneData, SyncAction } from '@spine-workspace/shared';

const DB_NAME = 'spine-workspace-offline';
const DB_VERSION = 1;

interface SceneRecord {
  id: string;
  sceneData: SceneData;
  localVersion: number;
  serverVersion: number;
  lastModified: number;
}

interface AssetRecord {
  id: string;
  metadata: unknown;
  jsonBlob: Blob;
  atlasBlob: Blob;
  pngBlobs: Blob[];
}

let db: IDBPDatabase | null = null;

async function getDB(): Promise<IDBPDatabase> {
  if (db) return db;
  db = await openDB(DB_NAME, DB_VERSION, {
    upgrade(database) {
      database.createObjectStore('scenes', { keyPath: 'id' });
      database.createObjectStore('assets', { keyPath: 'id' });
      database.createObjectStore('syncQueue', { keyPath: 'id', autoIncrement: false });
    },
  });
  return db;
}

// ---- Scenes ----

export async function getCachedScene(id: string): Promise<SceneData | null> {
  const database = await getDB();
  const record = await database.get('scenes', id) as SceneRecord | undefined;
  return record?.sceneData ?? null;
}

export async function saveSceneLocally(
  id: string,
  sceneData: SceneData,
  localVersion: number,
  serverVersion = 0
): Promise<void> {
  const database = await getDB();
  await database.put('scenes', {
    id,
    sceneData,
    localVersion,
    serverVersion,
    lastModified: Date.now(),
  });
}

export async function getSceneVersions(id: string): Promise<{ local: number; server: number } | null> {
  const database = await getDB();
  const record = await database.get('scenes', id) as SceneRecord | undefined;
  if (!record) return null;
  return { local: record.localVersion, server: record.serverVersion };
}

export async function listCachedSceneIds(): Promise<string[]> {
  const database = await getDB();
  return database.getAllKeys('scenes') as Promise<string[]>;
}

// ---- Assets ----

export async function saveAssetLocally(
  id: string,
  metadata: unknown,
  jsonBlob: Blob,
  atlasBlob: Blob,
  pngBlobs: Blob[]
): Promise<void> {
  const database = await getDB();
  await database.put('assets', { id, metadata, jsonBlob, atlasBlob, pngBlobs });
}

export async function getCachedAsset(id: string): Promise<AssetRecord | null> {
  const database = await getDB();
  return (await database.get('assets', id)) as AssetRecord | null;
}

// ---- Sync queue ----

export async function queueSync(action: SyncAction['action'], payload: unknown): Promise<void> {
  const database = await getDB();
  const item: SyncAction = {
    id: `${Date.now()}-${Math.random()}`,
    action,
    payload,
    timestamp: Date.now(),
    status: 'pending',
  };
  await database.put('syncQueue', item);
}

export async function getPendingSyncs(): Promise<SyncAction[]> {
  const database = await getDB();
  const all = await database.getAll('syncQueue') as SyncAction[];
  return all.filter((s) => s.status === 'pending');
}

export async function markSyncDone(id: string): Promise<void> {
  const database = await getDB();
  const item = await database.get('syncQueue', id) as SyncAction | undefined;
  if (item) await database.put('syncQueue', { ...item, status: 'done' });
}

export async function processSyncQueue(
  onSync: (action: SyncAction) => Promise<void>
): Promise<{ synced: number; failed: number }> {
  const pending = await getPendingSyncs();
  let synced = 0, failed = 0;

  for (const item of pending) {
    try {
      await onSync(item);
      await markSyncDone(item.id);
      synced++;
    } catch {
      failed++;
    }
  }

  return { synced, failed };
}
